// crm.ts — Patch MCC-4b. Contact-activity client: per-contact timeline
// (composed from activities + comms + sends + leads), note/call/task logging,
// and the cross-contact follow-ups queue.

import { request } from './request'

export interface TimelineItem {
  source: 'activity' | 'client_comm' | 'email' | 'lead'
  source_rank: number
  id: string
  kind: string
  occurred_at: string
  title: string | null
  body: string | null
  status: string | null
  direction: string | null
  due_at: string | null
  completed_at: string | null
}

export interface TimelineResponse {
  ok: boolean
  total_returned: number
  timeline: TimelineItem[]
  sources_capped: string[]
}

export type ActivityType = 'note' | 'call' | 'task' | 'meeting'

export interface FollowUp {
  id: string
  profile_id: string
  contact_name: string
  body: string | null
  due_at: string | null
  overdue: boolean
  created_at: string
}

export interface SmartListRule {
  cohort_id?: string
  tag?: string
  lifecycle_stage?: string
  no_touch_days?: number
  has_open_task?: boolean
  emailable?: boolean
}

export interface SmartListSummary {
  id: string
  name: string
  description: string | null
  rule: SmartListRule
  count: number | null
  rule_error: string | null
  created_at: string
}

export interface SmartListMember {
  id: string
  first_name: string | null
  last_name: string | null
  email_primary: string | null
  phone_mobile: string | null
  current_address_city: string | null
  lifecycle_stage: string
}

export const crmApi = {
  timeline: (profileId: string, limit?: number) =>
    request<TimelineResponse>(
      `/api/admin/profiles/${profileId}/timeline${limit ? `?limit=${limit}` : ''}`,
    ),

  addActivity: (
    profileId: string,
    input: { type: ActivityType; body?: string; direction?: 'inbound' | 'outbound'; due_at?: string },
  ) =>
    request<{ ok: boolean; id: string }>(`/api/admin/profiles/${profileId}/activities`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateActivity: (
    id: string,
    patch: { body?: string; due_at?: string; completed?: boolean },
  ) =>
    request<{ ok: boolean; id: string }>(`/api/admin/activities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteActivity: (id: string) =>
    request<{ ok: boolean; removed: boolean }>(`/api/admin/activities/${id}`, {
      method: 'DELETE',
    }),

  followUps: (window: 'overdue' | 'today' | 'week' | 'all' = 'all', limit?: number) => {
    const params = new URLSearchParams()
    if (window !== 'all') params.set('window', window)
    if (limit) params.set('limit', String(limit))
    const qs = params.toString()
    return request<{ ok: boolean; window: string; count: number; follow_ups: FollowUp[] }>(
      `/api/admin/follow-ups${qs ? `?${qs}` : ''}`,
    )
  },

  // --- MCC-4c: smart lists ---
  smartLists: () =>
    request<{ ok: boolean; smart_lists: SmartListSummary[] }>('/api/admin/smart-lists'),

  previewSmartList: (rule: SmartListRule, limit = 10) =>
    request<{ ok: boolean; total: number; sample: SmartListMember[] }>(
      '/api/admin/smart-lists/preview',
      { method: 'POST', body: JSON.stringify({ rule, limit }) },
    ),

  saveSmartList: (name: string, rule: SmartListRule, description?: string) =>
    request<{ ok: boolean; id: string }>('/api/admin/smart-lists', {
      method: 'POST',
      body: JSON.stringify({ name, rule, description }),
    }),

  smartListMembers: (id: string, limit?: number, offset?: number) => {
    const p = new URLSearchParams()
    if (limit) p.set('limit', String(limit))
    if (offset) p.set('offset', String(offset))
    const qs = p.toString()
    return request<{
      ok: boolean
      smart_list: { id: string; name: string }
      total: number
      members: SmartListMember[]
    }>(`/api/admin/smart-lists/${id}/members${qs ? `?${qs}` : ''}`)
  },

  deleteSmartList: (id: string) =>
    request<{ ok: boolean; removed: boolean }>(`/api/admin/smart-lists/${id}`, {
      method: 'DELETE',
    }),
}
